import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./App.css";
import Header from "./components/Header";
import TradingTableStrip from "./components/TradingTableStrip";
import DecisionDetailsCard from "./components/DecisionDetailsCard";
import SignalFilterPanel from "./components/SignalFilterPanel";
import SignalLogicGuide from "./components/SignalLogicGuide";
import PickSettingsPanel from "./components/PickSettingsPanel";
import StockCard from "./components/StockCard";
import StockDetailPage from "./components/StockDetailPage";
import EventConfigPage from "./components/EventConfigPage";
import MarketBiasCalculatorPage from "./components/MarketBiasCalculatorPage";
import { activateStrategyDraft, createStrategyDraft, fetchAdvisorDesk, fetchLatestAppliedMarketBias, fetchSavedTradeabilityDesk, fetchSignalLab, fetchStrategyDraft, fetchStrategyDrafts, fetchStrategyRuleConfig, fetchTradeabilityDesk, previewStrategy, refreshAdvisorDesk, refreshMarketBiasInputs, refreshOneTradeabilityCandidate, simulateStrategyDraftTradeability, updateStrategyDraft } from "./services/advisorService";
import { formatMarketBias, formatRiskLevel } from "./utils/marketLabels";
import { fetchNseWeeklyTrend } from "./services/marketDataRemote";
import { createDefaultScoringConfig, resolveSignalRuleName } from "./utils/signalScoringUtils";
import { getStrategyByCode, getStrategyByLabelOrCode } from "./utils/strategyLogicUtils";

function groupStocksBySectorIndustry(stocks) {
  return stocks.reduce((sectors, stock) => {
    const sector = stock.sector || "Unmapped Sector";
    const industry = stock.industry || "Unmapped Industry";

    if (!sectors[sector]) {
      sectors[sector] = { name: sector, count: 0, industries: {} };
    }
    if (!sectors[sector].industries[industry]) {
      sectors[sector].industries[industry] = { name: industry, count: 0, stocks: [] };
    }

    sectors[sector].count += 1;
    sectors[sector].industries[industry].count += 1;
    sectors[sector].industries[industry].stocks.push(stock);
    return sectors;
  }, {});
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function firstPresent(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== "");
}

function formatShortDate(value) {
  if (!value) return "-";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function deriveNseTone(row) {
  const averageChangePct = toNumber(firstPresent(row.averageChangePct, row.avgChangePct, row.equityAverageChangePct));
  const advancingCount = toNumber(firstPresent(row.advancingCount, row.advanceCount));
  const decliningCount = toNumber(firstPresent(row.decliningCount, row.declineCount));
  const stockCount = toNumber(firstPresent(row.stockCount, row.totalCount));
  const breadthRatio = stockCount ? advancingCount / stockCount : null;

  if ((averageChangePct !== null && averageChangePct >= 0.35) || (breadthRatio !== null && breadthRatio >= 0.58)) {
    return "Bullish";
  }
  if ((averageChangePct !== null && averageChangePct <= -0.35) || (breadthRatio !== null && breadthRatio <= 0.42)) {
    return "Bearish";
  }
  if (advancingCount !== null && decliningCount !== null && advancingCount > decliningCount) return "Positive";
  if (advancingCount !== null && decliningCount !== null && advancingCount < decliningCount) return "Negative";
  return "Mixed";
}

function normalizeNseWeeklyTrend(rows = []) {
  return rows
    .map((row) => {
      const date = firstPresent(row.mktDate, row.mkt_date, row.date);
      const averageChangePct = toNumber(firstPresent(row.averageChangePct, row.avgChangePct, row.equityAverageChangePct));

      return {
        date,
        label: formatShortDate(date),
        global: "-",
        nse: deriveNseTone(row),
        equityAverageChangePct: averageChangePct,
        stockCount: toNumber(firstPresent(row.stockCount, row.totalCount)),
        advancingCount: toNumber(firstPresent(row.advancingCount, row.advanceCount)),
        decliningCount: toNumber(firstPresent(row.decliningCount, row.declineCount)),
      };
    })
    .filter((note) => note.date)
    .slice(0, 5);
}

function buildWeeklyTrendNarration(notes = []) {
  if (!notes.length) return "Recent weekly movement is used as a background check before choosing an intraday plan.";

  const latest = notes[0];
  const pct = latest.equityAverageChangePct;
  const pctText = pct === null ? "" : ` (${pct.toFixed(2)}%)`;
  return `Last ${notes.length} NSE trading days are used as the weekly-to-date check; latest tone is ${latest.nse}${pctText}.`;
}

async function hydrateNseWeeklyTrend(data) {
  try {
    const recentMarketNotes = normalizeNseWeeklyTrend(await fetchNseWeeklyTrend(5));
    if (!recentMarketNotes.length) return data;

    return {
      ...data,
      marketBias: {
        ...data?.marketBias,
        recentMarketNotes,
        weeklyTrendNarration: buildWeeklyTrendNarration(recentMarketNotes),
      },
    };
  } catch (err) {
    console.warn("Unable to load NSE weekly trend.", err);
    return data;
  }
}

function compareText(left, right) {
  return String(left ?? "").localeCompare(String(right ?? ""));
}

function normalizeStrategyText(value) {
  return String(value ?? "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
}
const STRATEGY_SIGNAL_RULE_MAP = {
  MOMENTUM: "MOMENTUM_SCORE",
  TSI: "TSI",
  TREND: "TREND_SCORE",
  RELATIVE_STRENGTH: "RELATIVE_STRENGTH",
  RVOL: "RVOL",
  LIQUIDITY: "LIQUIDITY_SCORE",
  VOLATILITY: "VOLATILITY_SCORE",
  DRAWDOWN: "RECENT_DRAWDOWN",
};

function normalizeSignalText(value) {
  return String(value ?? "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function formatSignalDisplayValue(value) {
  return String(value ?? "").replace(/_/g, " ");
}

function toStrategyRuleName(signalGroup) {
  const key = String(signalGroup ?? "").trim().toUpperCase();
  return STRATEGY_SIGNAL_RULE_MAP[key] ?? key;
}

function buildBackendStrategySignalSelection(signals = [], ruleConfig = []) {
  const rulesByName = ruleConfig.reduce((rules, row) => {
    rules[toStrategyRuleName(row.signalGroup)] = row;
    return rules;
  }, {});

  return signals.reduce((selection, group) => {
    const resolvedRuleName = resolveSignalRuleName(group);
    const rule = rulesByName[resolvedRuleName];
    const selectedValues = Array.isArray(rule?.values)
      ? rule.values.filter((value) => value.selected).map((value) => value.signalValue)
      : rule?.selectedSignals ?? [];
    const wantedValues = new Set(selectedValues.map(normalizeSignalText));
    selection[group.id] = rule
      ? (group.values ?? []).filter((value) => wantedValues.has(normalizeSignalText(value)))
      : [];
    return selection;
  }, {});
}

function buildSignalGroupsFromStrategyConfig(ruleConfig = []) {
  return ruleConfig.map((row) => {
    const ruleName = toStrategyRuleName(row.signalGroup);
    return {
      id: ruleName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+(.)/g, (_, char) => char.toUpperCase())
        .replace(/[^a-zA-Z0-9]/g, ""),
      ruleName,
      signalGroup: row.signalGroup,
      label: String(row.signalGroup ?? ruleName).replace(/_/g, " "),
      values: (row.values ?? []).map((value) => value.signalValue),
      valueLabels: Object.fromEntries((row.values ?? []).map((value) => [
        value.signalValue,
        value.displayLabel ?? formatSignalDisplayValue(value.signalValue),
      ])),
      selectedValues: (row.values ?? [])
        .filter((value) => value.selected)
        .map((value) => value.signalValue),
    };
  });
}

function buildBackendScoringConfig(ruleConfig = []) {
  return ruleConfig.reduce((config, row) => {
    const ruleName = toStrategyRuleName(row.signalGroup);
    config[ruleName] = {
      weight: row.weight,
      threshold: row.thresholdCount,
      coreSignal: Boolean(row.coreSignal),
      selectedSignals: (row.values ?? []).filter((value) => value.selected).map((value) => value.signalValue),
    };
    return config;
  }, {});
}

function buildDraftRules(signals = [], selectedFilters = {}, scoringConfig = {}) {
  return signals.map((group) => {
    const ruleName = resolveSignalRuleName(group);
    const config = scoringConfig[ruleName] ?? {};
    return {
      signalGroup: group.signalGroup ?? ruleName,
      selectedSignals: selectedFilters[group.id] ?? [],
      availableSignals: group.values ?? [],
      weight: Number(config.weight ?? 0),
      thresholdCount: Number(config.threshold ?? 0),
      coreSignal: Boolean(config.coreSignal),
    };
  });
}

function mergeDraftRulesWithSourceCatalog(draftRules = [], sourceRules = []) {
  const draftByName = new Map(draftRules.map((rule) => [toStrategyRuleName(rule.signalGroup), rule]));
  const merged = sourceRules.map((sourceRule) => {
    const draftRule = draftByName.get(toStrategyRuleName(sourceRule.signalGroup));
    if (!draftRule) {
      return {
        ...sourceRule,
        coreSignal: false,
        values: (sourceRule.values ?? []).map((value) => ({ ...value, selected: false })),
      };
    }
    draftByName.delete(toStrategyRuleName(sourceRule.signalGroup));
    const selected = new Set((draftRule.selectedSignals ?? []).map(normalizeSignalText));
    const available = new Map((sourceRule.values ?? []).map((value) => [
      normalizeSignalText(value.signalValue),
      { ...value, selected: selected.has(normalizeSignalText(value.signalValue)) },
    ]));
    (draftRule.availableSignals ?? []).forEach((signalValue) => {
      const key = normalizeSignalText(signalValue);
      if (!available.has(key)) available.set(key, { signalValue, selected: selected.has(key) });
    });
    return { ...sourceRule, ...draftRule, values: [...available.values()] };
  });
  draftByName.forEach((draftRule) => {
    const selected = new Set((draftRule.selectedSignals ?? []).map(normalizeSignalText));
    merged.push({
      ...draftRule,
      values: (draftRule.availableSignals ?? []).map((signalValue) => ({
        signalValue,
        selected: selected.has(normalizeSignalText(signalValue)),
      })),
    });
  });
  return merged;
}

function clampPercent(value, min = 5, max = 95) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.max(min, Math.min(max, Math.round(number)));
}

function deriveOpeningLabel(marketBias, riskLevel) {
  const bias = normalizeStrategyText(marketBias);
  const risk = normalizeStrategyText(riskLevel);

  if (bias.includes("POSITIVE") && !bias.includes("NEUTRAL")) return "Positive open";
  if (bias.includes("NEUTRAL") && bias.includes("POSITIVE")) return "Flat to positive";
  if (bias.includes("NEUTRAL") && bias.includes("NEGATIVE")) return "Flat to weak";
  if (bias.includes("NEGATIVE") || risk.includes("HIGH")) return "Weak / cautious";
  if (bias.includes("NEUTRAL")) return "Flat / mixed";

  return "Watch first move";
}

function deriveMarketTopMetrics(summary = {}, existingMetrics = {}) {
  const existingInputs = existingMetrics.inputs ?? {};
  const existingNarrationInput = existingMetrics.narrationInput ?? {};
  const globalScore = Number(firstPresent(summary.globalScore, existingMetrics.globalScore, existingInputs.globalScore));
  const riskLevel = firstPresent(summary.riskLevel, existingMetrics.riskLevel, existingInputs.riskLevel);
  const marketBias = firstPresent(summary.marketBias, existingMetrics.bias, existingInputs.bias);
  const risk = normalizeStrategyText(riskLevel);
  const bias = normalizeStrategyText(marketBias);
  const usableScore = Number.isFinite(globalScore) ? globalScore : 5;
  const supportiveScore = clampPercent(100 - usableScore * 10, 15, 90);
  const riskAdd = risk.includes("HIGH") ? 22 : risk.includes("MODERATE") ? 10 : 0;
  const reversalRisk = clampPercent(usableScore * 8 + riskAdd, 15, 85);
  const sidewaysBase = bias.includes("NEUTRAL") ? 48 : 32;
  const sidewaysRiskAdd = risk.includes("MODERATE") ? 7 : risk.includes("HIGH") ? 12 : 0;

  return {
    opening: firstPresent(
      existingMetrics.opening,
      existingInputs.opening,
      existingNarrationInput.opening,
      deriveOpeningLabel(marketBias, riskLevel)
    ),
    confidence: firstPresent(
      existingMetrics.confidence,
      existingInputs.confidence,
      existingNarrationInput.confidence,
      supportiveScore
    ),
    trendDayProbability: firstPresent(
      existingMetrics.trendDayProbability,
      existingInputs.trendDayProbability,
      existingNarrationInput.trendDayProbability,
      supportiveScore
    ),
    sidewaysProbability: firstPresent(
      existingMetrics.sidewaysProbability,
      existingInputs.sidewaysProbability,
      existingNarrationInput.sidewaysProbability,
      clampPercent(sidewaysBase + sidewaysRiskAdd - Math.max(0, supportiveScore - 65) / 2, 20, 75)
    ),
    reversalRisk: firstPresent(
      existingMetrics.reversalRisk,
      existingInputs.reversalRisk,
      existingNarrationInput.reversalRisk,
      reversalRisk
    ),
  };
}

function normalizeMarketBiasTopMetrics(marketBias = {}, strategyRecommendation = {}, summary = {}) {
  const strategyInputs = strategyRecommendation.inputs ?? {};
  const topMetrics = deriveMarketTopMetrics(summary, {
    ...strategyInputs,
    ...marketBias,
    inputs: strategyInputs,
  });

  return {
    ...marketBias,
    bias: firstPresent(marketBias.bias, strategyInputs.bias, summary.marketBias),
    riskLevel: firstPresent(marketBias.riskLevel, strategyInputs.riskLevel, summary.riskLevel),
    ...topMetrics,
  };
}

function normalizeAdvisorData(data) {
  if (!data) return data;

  return {
    ...data,
    marketBias: normalizeMarketBiasTopMetrics(data.marketBias, data.strategyRecommendation),
  };
}

function summarizeMarketBiasSection(section) {
  const items = Array.isArray(section?.items) ? section.items : [];
  const itemCount = items.reduce((total, item) => total + (Number(item?.newsCount) || 1), 0);
  const scoreText = section?.sectionScore === null || section?.sectionScore === undefined
    ? "not scored"
    : `score ${section.sectionScore}`;

  if (!itemCount) {
    return `${section?.sectionName ?? "Market input"} has no active records in the latest applied state; it is treated as ${scoreText}.`;
  }

  return `${section?.sectionName ?? "Market input"} contributed ${itemCount} input${itemCount === 1 ? "" : "s"} with ${scoreText}.`;
}

function buildMarketBiasFromSignalLab(signalLabData, summary) {
  const sections = Array.isArray(signalLabData?.marketBiasSections) ? signalLabData.marketBiasSections : [];
  const topMetrics = deriveMarketTopMetrics(summary, signalLabData?.marketBias);
  const sectionByCode = sections.reduce((map, section) => {
    map[String(section?.sectionCode ?? "").toUpperCase()] = section;
    return map;
  }, {});

  const getSectionItems = (code) => sectionByCode[code]?.items ?? [];
  const marketRows = [
    ...getSectionItems("ETF_INPUTS"),
    ...getSectionItems("COMMODITY_INPUTS"),
  ].map((item) => ({
    ...item,
    name: item.symbol ?? item.sourceCode ?? item.headline,
    price: item.currentValue,
    previousClose: item.previousValue,
    change: item.changeValue,
    changePct: item.changePercent,
    currency: item.currency ?? "USD",
  }));

  return {
    bias: summary.marketBias,
    riskLevel: summary.riskLevel,
    ...topMetrics,
    summary: `Market score ${summary.globalScore ?? "-"}: ${summary.strategyDirection ?? "strategy pending"}`,
    marketBiasNarration: `Market score ${summary.globalScore ?? "-"}: ${summary.marketBias ?? "-"} with ${summary.riskLevel ?? "-"} risk.`,
    openingNarration: `${topMetrics.opening}; wait for price and volume confirmation before acting.`,
    riskNarration: `${summary.riskLevel ?? "Unscored"} risk from applied market score ${summary.globalScore ?? "-"}.`,
    globalSupportNarration: `Global news, ETF confirmation, India news, and commodities are combined into the applied market score.`,
    tradingInstruction: `${summary.strategyDirection ?? "Strategy pending"} plan uses ${summary.selectedStrategyName ?? "the selected strategy"} as the primary workflow.`,
    weeklyTrendNarration: "Recent weekly movement is used as a background check before choosing an intraday plan.",
    globalScore: summary.globalScore,
    eventScore: summary.eventScore,
    globalNewsScore: summary.globalNewsScore,
    indiaNewsScore: summary.indiaNewsScore,
    newsScore: summary.newsScore,
    etfScore: summary.etfScore,
    commodityScore: summary.commodityScore,
    strategyDirection: summary.strategyDirection,
    selectedStrategyCode: summary.selectedStrategyCode,
    marketRows,
    marketBiasSections: sections,
    eventImpact: {
      facts: [
        summarizeMarketBiasSection(sectionByCode.MAINTAINED_EVENT_NEWS),
        ...getSectionItems("MAINTAINED_EVENT_NEWS").map((item) => item.adminNotes ?? item.summary ?? item.headline).filter(Boolean),
      ].filter(Boolean),
    },
    indiaBiasDetails: {
      facts: [
        summarizeMarketBiasSection(sectionByCode.INDIA_NEWS),
        ...getSectionItems("INDIA_NEWS").map((item) => item.adminNotes ?? item.summary ?? item.headline).filter(Boolean),
      ].filter(Boolean),
    },
    reasons: [
      summarizeMarketBiasSection(sectionByCode.GLOBAL_NEWS),
      ...getSectionItems("GLOBAL_NEWS").map((item) => item.adminNotes ?? item.summary ?? item.headline).filter(Boolean),
    ].filter(Boolean),
  };
}

function buildAdvisorDataFromSignalLab(signalLabData) {
  const summary = signalLabData?.appliedMarketBias?.summary ?? {};
  const selectedStrategy = signalLabData?.selectedStrategy ?? {};
  const signalRules = signalLabData?.signalRules ?? [];
  const currentStrategy = getStrategyByCode(selectedStrategy.strategyCode ?? summary.selectedStrategyCode);
  const strategySide = String(selectedStrategy.strategySide ?? "").toUpperCase();
  const secondaryStrategies = (signalLabData?.strategies ?? [])
    .filter((item) => item?.strategyCode && item.strategyCode !== selectedStrategy.strategyCode)
    .filter((item) => String(item.strategySide ?? "").toUpperCase() === strategySide)
    .map((item) => item.strategyName ?? item.strategyCode)
    .slice(0, 3);
  const avoidStrategies = (signalLabData?.strategies ?? [])
    .filter((item) => String(item.strategySide ?? "").toUpperCase() !== strategySide)
    .map((item) => item.strategyName ?? item.strategyCode)
    .slice(0, 3);
  const actions = signalRules
    .filter((rule) => rule.coreSignal)
    .map((rule) => {
      const selected = (rule.values ?? [])
        .filter((value) => value.selected)
        .map((value) => formatSignalDisplayValue(value.signalValue))
        .join(", ");
      return `${String(rule.signalGroup ?? "").replace(/_/g, " ")} needs ${rule.passRule ?? `at least ${rule.thresholdCount ?? 0} matches`}${selected ? ` from ${selected}` : ""}.`;
    });

  return {
    marketBias: buildMarketBiasFromSignalLab(signalLabData, summary),
    strategyRecommendation: {
      code: selectedStrategy.strategyCode ?? summary.selectedStrategyCode,
      strategyCode: selectedStrategy.strategyCode ?? summary.selectedStrategyCode,
      primaryStrategy: selectedStrategy.strategyName ?? summary.selectedStrategyName,
      label: selectedStrategy.strategyName ?? summary.selectedStrategyName,
      selectedStrategyLabel: selectedStrategy.strategyName ?? summary.selectedStrategyName,
      mode: selectedStrategy.strategySide === "SELL" ? "Defensive" : "Balanced",
      minScore: 60,
      positionSizing: selectedStrategy.positionSizingNote,
      entryRule: selectedStrategy.entryRule ?? currentStrategy.stockRule,
      exitRule: selectedStrategy.exitRule ?? selectedStrategy.riskNote ?? currentStrategy.businessRule,
      reason: selectedStrategy.strategySummary ?? `Selected by Market Bias score ${summary.globalScore ?? "-"}.`,
      secondaryStrategies,
      avoidStrategies,
      actions,
    },
    signals: buildSignalGroupsFromStrategyConfig(signalRules),
    signalSource: "SIGNAL_LAB",
  };
}

function TradeCandidatesLoadingMask() {
  return (
    <div className="logic-loading-mask app-transition-mask" role="status" aria-live="polite">
      <div className="logic-loading-panel">
        <span className="logic-loading-spinner" aria-hidden="true" />
        <strong>Loading trade candidates</strong>
        <p>
          Checking market bias, selected strategy rules, signal values, score thresholds, and price settings before updating the matching stock list.
        </p>
      </div>
    </div>
  );
}

const CANDIDATE_MASK_MIN_MS = 650;

function waitForMaskPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
      return;
    }

    setTimeout(resolve, 0);
  });
}

function formatLocalDate(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLatestCompletedWeekday(now = new Date()) {
  const completed = new Date(now);
  const afterClose = completed.getHours() > 15 || (completed.getHours() === 15 && completed.getMinutes() >= 30);
  if (completed.getDay() === 0) completed.setDate(completed.getDate() - 2);
  else if (completed.getDay() === 6) completed.setDate(completed.getDate() - 1);
  else if (!afterClose) completed.setDate(completed.getDate() - 1);
  if (completed.getDay() === 0) completed.setDate(completed.getDate() - 2);
  else if (completed.getDay() === 6) completed.setDate(completed.getDate() - 1);
  return completed;
}

function isNseMarketOpen(now = new Date()) {
  const day = now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();
  return day >= 1 && day <= 5 && minutes >= 9 * 60 + 15 && minutes <= 15 * 60 + 30;
}

function App() {
  const [advisorData, setAdvisorData] = useState(null);
  const [signalLabData, setSignalLabData] = useState(null);
  const [signalFilters, setSignalFilters] = useState({});
  const [matchingStocks, setMatchingStocks] = useState([]);
  const [tradeabilityDesk, setTradeabilityDesk] = useState(null);
  const [scoringConfig, setScoringConfig] = useState(createDefaultScoringConfig);
  const [minimumScore, setMinimumScore] = useState(60);
  const [strategyRuleConfig, setStrategyRuleConfig] = useState([]);
  const [strategyPreview, setStrategyPreview] = useState(null);
  const [strategyDrafts, setStrategyDrafts] = useState([]);
  const [editingStrategyDraft, setEditingStrategyDraft] = useState(null);
  const [savingStrategyDraft, setSavingStrategyDraft] = useState(false);
  const [simulatingStrategyDraft, setSimulatingStrategyDraft] = useState("");
  const [strategyDraftSimulation, setStrategyDraftSimulation] = useState(null);
  const [pickSettings, setPickSettings] = useState({ priceMin: 100, priceMax: 500 });
  const [loading, setLoading] = useState(true);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [stockRefreshLoading, setStockRefreshLoading] = useState(false);
  const [candidateMaskVisible, setCandidateMaskVisible] = useState(false);
  const [refreshingMarketInputs, setRefreshingMarketInputs] = useState(false);
  const [marketBiasInputs, setMarketBiasInputs] = useState(null);
  const [error, setError] = useState("");
  const [activePage, setActivePage] = useState("desk");
  const [activeStrategyFilter, setActiveStrategyFilter] = useState("ALL");
  const [opportunityStrategyCode, setOpportunityStrategyCode] = useState("");
  const [selectedGuideStrategyCode, setSelectedGuideStrategyCode] = useState("CUSTOM");
  const [selectedStock, setSelectedStock] = useState(null);
  const [lastSelectedSymbol, setLastSelectedSymbol] = useState(null);
  const [priceFilter, setPriceFilter] = useState({ min: "", max: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState("CONFIDENCE");
  const [collapsedSectors, setCollapsedSectors] = useState({});
  const [collapsedIndustries, setCollapsedIndustries] = useState({});
  const skipNextListFetch = useRef(false);
  const listHasLoaded = useRef(false);
  const autoListFetchKey = useRef("");
  const autoListFetchInFlight = useRef(false);
  const didLoadAdvisor = useRef(false);
  const signalGroupsForFilters = useMemo(() => (
    strategyRuleConfig.length
      ? buildSignalGroupsFromStrategyConfig(strategyRuleConfig)
      : advisorData?.signals ?? []
  ), [advisorData, strategyRuleConfig]);
  const activeStrategies = useMemo(() => signalLabData?.strategies ?? [], [signalLabData]);
  const resolveActiveStrategy = useCallback((strategyCode) => {
    const configured = activeStrategies.find((strategy) => strategy.strategyCode === strategyCode);
    if (configured) {
      return {
        code: configured.strategyCode,
        label: configured.strategyName ?? configured.strategyCode,
        action: configured.strategySide ?? "WATCH",
      };
    }
    return getStrategyByCode(strategyCode);
  }, [activeStrategies]);

  const loadAdvisor = async (refresh = false) => {
    setLoading(true);
    setError("");

    try {
      const signalLab = await fetchSignalLab();
      const hasAppliedSignalLab = signalLab?.selectedStrategy?.strategyCode && signalLab?.signalRules?.length;
      const baseData = hasAppliedSignalLab
        ? buildAdvisorDataFromSignalLab(signalLab)
        : refresh
          ? normalizeAdvisorData(await refreshAdvisorDesk())
          : normalizeAdvisorData(await fetchAdvisorDesk());
      const data = await hydrateNseWeeklyTrend(baseData);
      const primaryStrategy = hasAppliedSignalLab
        ? getStrategyByCode(signalLab.selectedStrategy.strategyCode)
        : getStrategyByLabelOrCode(data?.strategyRecommendation?.primaryStrategy);
      const primaryRuleConfig = hasAppliedSignalLab
        ? signalLab.signalRules
        : await fetchStrategyRuleConfig(primaryStrategy.code);
      if (!primaryRuleConfig.length) {
        throw new Error("Primary strategy configuration is empty.");
      }
      const primarySignalGroups = buildSignalGroupsFromStrategyConfig(primaryRuleConfig);
      setSignalLabData(hasAppliedSignalLab ? signalLab : null);
      setAdvisorData(data);
      setStrategyRuleConfig(primaryRuleConfig);
      setSignalFilters(buildBackendStrategySignalSelection(primarySignalGroups, primaryRuleConfig));
      setScoringConfig((current) => ({
        ...current,
        ...buildBackendScoringConfig(primaryRuleConfig),
      }));
      setSelectedGuideStrategyCode(primaryStrategy.code);
      setOpportunityStrategyCode(primaryStrategy.code);
      setMatchingStocks([]);
      setSelectedStock(null);
      setLastSelectedSymbol(null);
      setPriceFilter({ min: "", max: "" });
      setSearchTerm("");
      setSortMode("CONFIDENCE");
      listHasLoaded.current = false;
      autoListFetchKey.current = "";
    } catch (err) {
      console.error("Unable to load advisor desk", err);
      setError("Unable to load advisor data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didLoadAdvisor.current) return;
    didLoadAdvisor.current = true;
    loadAdvisor();
  }, []);

  const loadStrategyStocks = useCallback(async (override = {}) => {
    if (!advisorData?.signals?.length) return [];

    setStocksLoading(true);
    setError("");
    try {
      listHasLoaded.current = true;
      const selectedFilters = override.selectedFilters ?? signalFilters;
      const requestSignals = override.signals ?? signalGroupsForFilters;
      const strategy = override.strategy ?? advisorData.strategyRecommendation;
      const requestPickSettings = override.pickSettings ?? pickSettings;
      const savedDesk = await fetchSavedTradeabilityDesk({
        strategy,
        signals: requestSignals,
        selectedFilters,
        pickSettings: requestPickSettings,
      }).catch((err) => {
        console.warn("No saved tradeability state available.", err);
        return null;
      });
      if (savedDesk?.stocks?.length && !override.forceRebuild) {
        setTradeabilityDesk(savedDesk);
        setMatchingStocks(savedDesk.stocks);
        setSelectedStock(null);
        setLastSelectedSymbol(null);
        setPriceFilter({ min: "", max: "" });
        setSearchTerm("");
        setSortMode("CONFIDENCE");
        return savedDesk.stocks;
      }
      const desk = await fetchTradeabilityDesk({
        strategy,
        signals: requestSignals,
        selectedFilters,
        pickSettings: requestPickSettings,
      });
      const refreshedStocks = desk?.stocks ?? [];
      const savedStocks = savedDesk?.stocks ?? [];
      const stocks = refreshedStocks.length ? refreshedStocks : savedStocks;
      const resolvedDesk = refreshedStocks.length || !savedDesk
        ? desk
        : {
          ...desk,
          buckets: savedDesk.buckets ?? desk?.buckets ?? [],
          summary: savedDesk.summary ?? desk?.summary ?? {},
        };
      setTradeabilityDesk(resolvedDesk);
      setMatchingStocks(stocks);
      setSelectedStock(null);
      setLastSelectedSymbol(null);
      setPriceFilter({ min: "", max: "" });
      setSearchTerm("");
      setSortMode("CONFIDENCE");
      return stocks;
    } finally {
      setStocksLoading(false);
    }
  }, [advisorData, pickSettings, signalFilters, signalGroupsForFilters]);

  const buildPrimaryStrategyContext = useCallback(() => {
    const marketPrimaryStrategy = getStrategyByLabelOrCode(advisorData?.strategyRecommendation?.primaryStrategy);
    const primaryStrategy = resolveActiveStrategy(opportunityStrategyCode || marketPrimaryStrategy.code);
    const selectedFilters = buildBackendStrategySignalSelection(signalGroupsForFilters, strategyRuleConfig);

    return {
      primaryStrategy,
      selectedFilters,
      requestStrategy: {
        ...advisorData?.strategyRecommendation,
        code: primaryStrategy.code,
        strategyCode: primaryStrategy.code,
        primaryStrategy: primaryStrategy.label,
        label: primaryStrategy.label,
      },
    };
  }, [advisorData, opportunityStrategyCode, resolveActiveStrategy, signalGroupsForFilters, strategyRuleConfig]);

  const overrideOpportunityStrategy = async (strategyCode) => {
    if (!strategyCode || strategyCode === opportunityStrategyCode) return;
    setStocksLoading(true);
    setError("");
    try {
      const ruleConfig = await fetchStrategyRuleConfig(strategyCode);
      if (!ruleConfig.length) throw new Error("The selected strategy has no active rule configuration.");
      const strategy = resolveActiveStrategy(strategyCode);
      const groups = buildSignalGroupsFromStrategyConfig(ruleConfig);
      const selectedFilters = buildBackendStrategySignalSelection(groups, ruleConfig);
      setOpportunityStrategyCode(strategyCode);
      setStrategyRuleConfig(ruleConfig);
      setSignalFilters(selectedFilters);
      setScoringConfig((current) => ({ ...current, ...buildBackendScoringConfig(ruleConfig) }));
      setActiveStrategyFilter(strategyCode);
      setSelectedGuideStrategyCode(strategyCode);
      skipNextListFetch.current = true;
      autoListFetchKey.current = "";
      await loadStrategyStocks({
        strategy: {
          ...advisorData?.strategyRecommendation,
          code: strategy.code,
          strategyCode: strategy.code,
          primaryStrategy: strategy.label,
          label: strategy.label,
        },
        signals: groups,
        selectedFilters,
        forceRebuild: true,
      });
    } catch (err) {
      console.error("Unable to override the opportunity strategy.", err);
      setError(err?.message || "Unable to load the selected strategy.");
    } finally {
      setStocksLoading(false);
    }
  };

  useEffect(() => {
    const strategyCode = tradeabilityDesk?.strategyCode;
    if (activePage !== "list" || !strategyCode || strategyCode === "CUSTOM" || !signalGroupsForFilters.length) {
      return undefined;
    }

    let cancelled = false;
    let refreshInFlight = false;
    const refreshSavedDesk = async () => {
      if (refreshInFlight) return;
      refreshInFlight = true;
      try {
        const selectedStrategy = resolveActiveStrategy(strategyCode);
        const savedDesk = await fetchSavedTradeabilityDesk({
          strategy: {
            ...advisorData?.strategyRecommendation,
            code: strategyCode,
            strategyCode,
            primaryStrategy: selectedStrategy.label,
            label: selectedStrategy.label,
          },
          signals: signalGroupsForFilters,
          selectedFilters: signalFilters,
          pickSettings,
        });
        if (!cancelled && savedDesk?.stocks?.length) {
          setTradeabilityDesk(savedDesk);
          setMatchingStocks(savedDesk.stocks);
          setSelectedStock((current) => {
            if (!current?.symbol) return current;
            return savedDesk.stocks.find((stock) => stock.symbol === current.symbol) ?? current;
          });
        }
      } catch (err) {
        console.warn("Unable to refresh saved tradeability state.", err);
      } finally {
        refreshInFlight = false;
      }
    };

    const intervalId = setInterval(refreshSavedDesk, 30000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [activePage, advisorData, pickSettings, resolveActiveStrategy, signalFilters, signalGroupsForFilters, tradeabilityDesk?.strategyCode]);

  useEffect(() => {
    if (activePage !== "list" || !signalGroupsForFilters.length || loading || listHasLoaded.current) return;
    if (!strategyRuleConfig.length) {
      setError("Unable to load strategy configuration. Please try again later.");
      return;
    }
    if (skipNextListFetch.current) {
      skipNextListFetch.current = false;
      return;
    }

    const { primaryStrategy, selectedFilters, requestStrategy } = buildPrimaryStrategyContext();
    const requestKey = JSON.stringify({
      strategyCode: primaryStrategy.code,
      signalCount: signalGroupsForFilters.length,
      priceMin: pickSettings.priceMin,
      priceMax: pickSettings.priceMax,
    });

    if (autoListFetchInFlight.current || autoListFetchKey.current === requestKey) return;
    autoListFetchInFlight.current = true;
    autoListFetchKey.current = requestKey;

    const timeout = setTimeout(async () => {
      try {
        setActiveStrategyFilter(primaryStrategy.code);
        setSignalFilters(selectedFilters);
        setSelectedGuideStrategyCode(primaryStrategy.code);
        await loadStrategyStocks({
          strategy: requestStrategy,
          selectedFilters,
        });
      } catch (err) {
        console.error("Unable to load stock picks.", err);
        setError("Unable to load stock picks. Please try again later.");
      } finally {
        autoListFetchInFlight.current = false;
      }
    }, 350);

    return () => {
      clearTimeout(timeout);
      autoListFetchInFlight.current = false;
    };
  }, [activePage, advisorData, buildPrimaryStrategyContext, loadStrategyStocks, loading, matchingStocks.length, pickSettings, signalGroupsForFilters.length, strategyRuleConfig.length]);

  const visibleStocks = useMemo(() => {
    const deskStrategy = normalizeStrategyText(tradeabilityDesk?.strategyCode);
    const activeStrategyCode = normalizeStrategyText(activeStrategyFilter);
    if (activeStrategyFilter === "ALL" || (deskStrategy && deskStrategy === activeStrategyCode)) {
      return matchingStocks;
    }
    const activeStrategy = resolveActiveStrategy(activeStrategyFilter);
    const acceptedValues = [
      activeStrategyFilter,
      activeStrategy.code,
      activeStrategy.label,
      activeStrategy.action,
      advisorData?.strategyRecommendation?.primaryStrategy,
    ].map(normalizeStrategyText).filter(Boolean);

    return matchingStocks.filter((stock) =>
      acceptedValues.some((value) => {
        const strategy = normalizeStrategyText(stock.strategy);
        const action = normalizeStrategyText(stock.action);
        return (
          strategy === value ||
          action === value
        );
      })
    );
  }, [activeStrategyFilter, advisorData, matchingStocks, resolveActiveStrategy, tradeabilityDesk]);

  const priceBounds = useMemo(() => {
    const prices = visibleStocks
      .map((stock) => toNumber(stock.price))
      .filter((price) => price !== null);

    if (!prices.length) return { min: 0, max: 0 };

    return {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices)),
    };
  }, [visibleStocks]);

  const activePriceMin = priceFilter.min === "" ? priceBounds.min : Number(priceFilter.min);
  const activePriceMax = priceFilter.max === "" ? priceBounds.max : Number(priceFilter.max);

  const priceFilteredStocks = useMemo(() => {
    const min = Number.isFinite(activePriceMin) ? activePriceMin : priceBounds.min;
    const max = Number.isFinite(activePriceMax) ? activePriceMax : priceBounds.max;

    return visibleStocks.filter((stock) => {
      const price = toNumber(stock.price);
      if (price === null) return false;
      return price >= min && price <= max;
    });
  }, [activePriceMax, activePriceMin, priceBounds.max, priceBounds.min, visibleStocks]);

  const searchedStocks = useMemo(() => {
    const query = searchTerm.trim().toUpperCase();
    if (!query) return priceFilteredStocks;

    return priceFilteredStocks.filter((stock) =>
      String(stock.symbol ?? "").toUpperCase().includes(query)
    );
  }, [priceFilteredStocks, searchTerm]);

  const filteredStocks = useMemo(() => {
    return [...searchedStocks].sort((left, right) => {
      if (sortMode === "PRICE") {
        return (toNumber(left.price) ?? Number.MAX_VALUE) - (toNumber(right.price) ?? Number.MAX_VALUE);
      }
      if (sortMode === "SYMBOL") {
        return compareText(left.symbol, right.symbol);
      }

      return (toNumber(right.confidenceScore) ?? -1) - (toNumber(left.confidenceScore) ?? -1);
    });
  }, [searchedStocks, sortMode]);

  const groupedStocks = useMemo(() => groupStocksBySectorIndustry(filteredStocks), [filteredStocks]);

  const changePriceFilter = (field, value) => {
    const numericValue = Number(value);
    setPriceFilter((current) => {
      const next = { ...current, [field]: numericValue };
      const min = field === "min" ? numericValue : Number(next.min === "" ? priceBounds.min : next.min);
      const max = field === "max" ? numericValue : Number(next.max === "" ? priceBounds.max : next.max);

      if (field === "min" && min > max) next.max = numericValue;
      if (field === "max" && max < min) next.min = numericValue;

      return next;
    });
  };

  const resetListFilters = () => {
    setPriceFilter({ min: "", max: "" });
    setSearchTerm("");
    setSortMode("CONFIDENCE");
  };

  const toggleSector = (sectorName) => {
    setCollapsedSectors((current) => ({ ...current, [sectorName]: !current[sectorName] }));
  };

  const toggleIndustry = (industryKey) => {
    setCollapsedIndustries((current) => ({ ...current, [industryKey]: !current[industryKey] }));
  };

  const reloadOpportunityCandidates = async (settingsOverride) => {
    try {
      await runCandidateTransition(async () => {
        const requestPickSettings = settingsOverride ?? pickSettings;
        if (settingsOverride) setPickSettings(settingsOverride);
        const { primaryStrategy, selectedFilters, requestStrategy } = buildPrimaryStrategyContext();
        setActiveStrategyFilter(primaryStrategy.code);
        setSignalFilters(selectedFilters);
        setSelectedGuideStrategyCode(primaryStrategy.code);
        skipNextListFetch.current = true;
        await loadStrategyStocks({
          strategy: requestStrategy,
          selectedFilters,
          pickSettings: requestPickSettings,
          forceRebuild: true,
        });
      });
    } catch (err) {
      console.error("Unable to reload stock picks.", err);
      setError("Unable to reload stock picks. Please try again later.");
    }
  };

  useEffect(() => {
    setStrategyPreview(null);
  }, [minimumScore, pickSettings, scoringConfig, selectedGuideStrategyCode, signalFilters]);

  useEffect(() => {
    if (activePage !== "guide") return;
    fetchStrategyDrafts().then(setStrategyDrafts).catch((err) => console.warn("Unable to load strategy drafts.", err));
  }, [activePage]);

  const previewSignalStrategy = async () => {
    const rules = buildDraftRules(signalGroupsForFilters, signalFilters, scoringConfig);
    const maximumScore = rules.reduce((total, rule) => total + Number(rule.weight || 0), 0);
    const requestedMinimumScore = Number(minimumScore);
    if (!Number.isFinite(requestedMinimumScore) || requestedMinimumScore < 0) {
      setError("Minimum score must be zero or greater.");
      return;
    }
    if (requestedMinimumScore > maximumScore) {
      setError(`Minimum score cannot exceed the maximum achievable score of ${maximumScore}.`);
      return;
    }
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 90);
    setStocksLoading(true);
    setError("");
    try {
      const preview = await previewStrategy({
        sourceStrategyCode: selectedGuideStrategyCode,
        dateFrom: from.toISOString().slice(0, 10),
        dateTo: today.toISOString().slice(0, 10),
        sectorId: "AL",
        industryId: "AL1",
        priceMin: pickSettings.priceMin,
        priceMax: pickSettings.priceMax,
        excludeNIZ: true,
        mode: advisorData?.strategyRecommendation?.mode ?? "BALANCED",
        minimumScore: requestedMinimumScore,
        rules,
      });
      setStrategyPreview(preview);
      setTradeabilityDesk(null);
      setMatchingStocks(preview.candidates ?? []);
      setActiveStrategyFilter(selectedGuideStrategyCode);
      setSelectedStock(null);
      setLastSelectedSymbol(null);
      setPriceFilter({ min: "", max: "" });
      setSearchTerm("");
      setSortMode("CONFIDENCE");
      listHasLoaded.current = true;
      setActivePage("list");
    } catch (err) {
      setError(err?.message || "Unable to preview this strategy configuration.");
    } finally {
      setStocksLoading(false);
    }
  };

  const saveSignalStrategyDraft = async (metadata) => {
    if (!strategyPreview) return;
    setSavingStrategyDraft(true);
    setError("");
    try {
      const request = {
        ...metadata,
        sourceStrategyCode: selectedGuideStrategyCode,
        createdBy: "portal-admin",
        previewId: strategyPreview.previewId,
        configurationHash: strategyPreview.configurationHash,
        rules: buildDraftRules(signalGroupsForFilters, signalFilters, scoringConfig),
      };
      const saved = editingStrategyDraft
        ? await updateStrategyDraft(editingStrategyDraft.strategyCode, request)
        : await createStrategyDraft(request);
      setStrategyDrafts((current) => [saved, ...current.filter((draft) => draft.strategyCode !== saved.strategyCode)]);
      setEditingStrategyDraft(saved);
    } catch (err) {
      setError(err?.message || "Unable to save strategy draft.");
    } finally {
      setSavingStrategyDraft(false);
    }
  };

  const editSignalStrategyDraft = async (strategyCode) => {
    setError("");
    try {
      const draft = await fetchStrategyDraft(strategyCode);
      const sourceRules = await fetchStrategyRuleConfig(draft.sourceStrategyCode);
      const ruleConfig = mergeDraftRulesWithSourceCatalog(draft.rules ?? [], sourceRules);
      const groups = buildSignalGroupsFromStrategyConfig(ruleConfig);
      setEditingStrategyDraft(draft);
      setSelectedGuideStrategyCode(draft.sourceStrategyCode);
      setStrategyRuleConfig(ruleConfig);
      setSignalFilters(buildBackendStrategySignalSelection(groups, ruleConfig));
      setScoringConfig((current) => ({ ...current, ...buildBackendScoringConfig(ruleConfig) }));
    } catch (err) {
      setError(err?.message || "Unable to load strategy draft.");
    }
  };

  const activateSignalStrategyDraft = async (strategyCode) => {
    setSavingStrategyDraft(true);
    setError("");
    try {
      await activateStrategyDraft(strategyCode);
      setStrategyDrafts((current) => current.filter((draft) => draft.strategyCode !== strategyCode));
      if (editingStrategyDraft?.strategyCode === strategyCode) setEditingStrategyDraft(null);
      await loadAdvisor();
    } catch (err) {
      setError(err?.message || "Unable to activate strategy draft.");
    } finally {
      setSavingStrategyDraft(false);
    }
  };

  const simulateSignalStrategyDraft = async (strategyCode) => {
    setSimulatingStrategyDraft(strategyCode);
    setStrategyDraftSimulation(null);
    setError("");
    try {
      const now = new Date();
      const dateTo = isNseMarketOpen(now) ? now : getLatestCompletedWeekday(now);
      const dateFrom = new Date(dateTo);
      dateFrom.setDate(dateFrom.getDate() - 90);
      const result = await simulateStrategyDraftTradeability(strategyCode, {
        dateFrom: formatLocalDate(dateFrom),
        dateTo: formatLocalDate(dateTo),
        sectorId: "AL",
        industryId: "AL1",
        priceMin: Number(pickSettings.priceMin) || 100,
        priceMax: Number(pickSettings.priceMax) || 500,
        excludeNIZ: true,
        mode: "Balanced",
        minimumScore: Number(minimumScore) || 30,
        evaluationStrategyCode: "INTRADAY_SHORT_BREAKDOWN",
      });
      setStrategyDraftSimulation(result);
    } catch (err) {
      setError(err?.message || "Unable to run the previous-close short simulation.");
    } finally {
      setSimulatingStrategyDraft("");
    }
  };

  const applyGuideStrategySignals = (strategyCode) => {
    if (!strategyRuleConfig.length) return;
    setSignalFilters(buildBackendStrategySignalSelection(signalGroupsForFilters, strategyRuleConfig));
  };

  const changeGuideStrategyLogic = async (strategyCode) => {
    setSelectedGuideStrategyCode(strategyCode);
    if (strategyCode === "CUSTOM") {
      setStrategyRuleConfig([]);
      return;
    }
    try {
      const ruleConfig = await fetchStrategyRuleConfig(strategyCode);
      if (!ruleConfig.length) {
        throw new Error(`Strategy configuration is empty: ${strategyCode}`);
      }
      setStrategyRuleConfig(ruleConfig);
      const configuredSignalGroups = buildSignalGroupsFromStrategyConfig(ruleConfig);
      setSignalFilters(buildBackendStrategySignalSelection(configuredSignalGroups, ruleConfig));
      setScoringConfig((current) => ({
        ...current,
        ...buildBackendScoringConfig(ruleConfig),
      }));
    } catch (ruleError) {
      console.error("Unable to load strategy rule config.", ruleError);
      setStrategyRuleConfig([]);
      setSignalFilters({});
      setError("Unable to load strategy configuration. Please try again later.");
    }
  };

  const runCandidateTransition = async (loadCandidates) => {
    const startedAt = Date.now();
    setCandidateMaskVisible(true);
    await waitForMaskPaint();

    try {
      await loadCandidates();
    } finally {
      const remainingMs = CANDIDATE_MASK_MIN_MS - (Date.now() - startedAt);
      if (remainingMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingMs));
      }
      setCandidateMaskVisible(false);
    }
  };

  const navigatePage = async (page) => {
    setSelectedStock(null);

    if (page === "list") {
      try {
        await runCandidateTransition(async () => {
          const { primaryStrategy, selectedFilters, requestStrategy } = buildPrimaryStrategyContext();
          setActiveStrategyFilter(primaryStrategy.code);
          setSignalFilters(selectedFilters);
          setSelectedGuideStrategyCode(primaryStrategy.code);
          setActivePage("list");
          skipNextListFetch.current = true;
          await loadStrategyStocks({
            strategy: requestStrategy,
            selectedFilters,
          });
        });
      } catch (err) {
        console.error("Unable to load stock picks.", err);
        setError("Unable to load stock picks. Please try again later.");
      }
      return;
    }

    if (page === "marketBiasCalculator") {
      setActivePage(page);
      if (!marketBiasInputs) {
        try {
          const latestApplied = await fetchLatestAppliedMarketBias();
          if (latestApplied?.status && latestApplied.status !== "NOT_APPLIED") {
            setMarketBiasInputs(latestApplied);
            await applyMarketBiasSummary(latestApplied.summary);
          }
        } catch (err) {
          console.warn("Unable to load latest applied market bias state.", err);
        }
      }
      return;
    }

    setActivePage(page);
  };

  const refreshMarketInputsForCalculator = async () => {
    setRefreshingMarketInputs(true);
    setError("");

    try {
      const inputs = await refreshMarketBiasInputs();
      setMarketBiasInputs(inputs);
      setActivePage("marketBiasCalculator");
    } catch (err) {
      console.error("Unable to refresh market bias inputs.", err);
      setError("Unable to refresh market inputs.");
    } finally {
      setRefreshingMarketInputs(false);
    }
  };

  const applyMarketBiasSummary = async (summaryOrState) => {
    const summary = summaryOrState?.summary ?? summaryOrState;
    const selectedStrategy = getStrategyByCode(summary?.selectedStrategyCode);
    const topMetrics = deriveMarketTopMetrics(summary, advisorData?.marketBias);
    const recentMarketNotes = normalizeNseWeeklyTrend(await fetchNseWeeklyTrend(5).catch((err) => {
      console.warn("Unable to load NSE weekly trend.", err);
      return [];
    }));
    if (summaryOrState?.summary) {
      setMarketBiasInputs(summaryOrState);
    }
    setAdvisorData((current) => ({
      ...current,
      marketBias: {
        ...current?.marketBias,
        bias: summary?.marketBias,
        riskLevel: summary?.riskLevel,
        ...topMetrics,
        summary: `Market score ${summary?.globalScore}: ${summary?.strategyDirection}`,
        marketBiasNarration: `Market score ${summary?.globalScore}: ${summary?.marketBias} with ${summary?.riskLevel} risk.`,
        openingNarration: `${topMetrics.opening}; wait for price and volume confirmation before acting.`,
        riskNarration: `${summary?.riskLevel ?? "Unscored"} risk from applied market score ${summary?.globalScore ?? "-"}.`,
        globalSupportNarration: "Global news, ETF confirmation, India news, and commodities are combined into the applied market score.",
        weeklyTrendNarration: recentMarketNotes.length
          ? buildWeeklyTrendNarration(recentMarketNotes)
          : current?.marketBias?.weeklyTrendNarration ?? "Recent weekly movement is used as a background check before choosing an intraday plan.",
        recentMarketNotes: recentMarketNotes.length ? recentMarketNotes : current?.marketBias?.recentMarketNotes,
        tradingInstruction: `${summary?.strategyDirection ?? "Strategy pending"} plan uses ${summary?.selectedStrategyName ?? selectedStrategy.label} as the primary workflow.`,
        globalScore: summary?.globalScore,
        eventScore: summary?.eventScore,
        globalNewsScore: summary?.globalNewsScore,
        indiaNewsScore: summary?.indiaNewsScore,
        newsScore: summary?.newsScore,
        etfScore: summary?.etfScore,
        commodityScore: summary?.commodityScore,
        strategyDirection: summary?.strategyDirection,
        selectedStrategyCode: summary?.selectedStrategyCode,
      },
      strategyRecommendation: {
        ...current?.strategyRecommendation,
        code: selectedStrategy.code,
        strategyCode: selectedStrategy.code,
        primaryStrategy: summary?.selectedStrategyName ?? selectedStrategy.label,
        label: summary?.selectedStrategyName ?? selectedStrategy.label,
        selectedStrategyLabel: summary?.selectedStrategyName ?? selectedStrategy.label,
        mode: selectedStrategy.code === "AVOID_WEAK" ? "Defensive" : current?.strategyRecommendation?.mode,
        minScore: summary?.minStockConfidence ?? current?.strategyRecommendation?.minScore,
        positionSizing: summary?.positionSizeRule ?? current?.strategyRecommendation?.positionSizing,
        reason: `Selected by Market Bias score ${summary?.globalScore}.`,
      },
    }));
    setSelectedGuideStrategyCode(selectedStrategy.code);
    if (selectedStrategy.code !== "CUSTOM") {
      const ruleConfig = await fetchStrategyRuleConfig(selectedStrategy.code);
      setStrategyRuleConfig(ruleConfig);
      const configuredSignalGroups = buildSignalGroupsFromStrategyConfig(ruleConfig);
      setSignalFilters(buildBackendStrategySignalSelection(configuredSignalGroups, ruleConfig));
      setScoringConfig((current) => ({
        ...current,
        ...buildBackendScoringConfig(ruleConfig),
      }));
      setActiveStrategyFilter(selectedStrategy.code);
    }
  };

  const openStrategyList = async (strategyCode) => {
    try {
      await runCandidateTransition(async () => {
        const selectedStrategy = getStrategyByLabelOrCode(strategyCode);
        const ruleConfig = await fetchStrategyRuleConfig(selectedStrategy.code);
        if (!ruleConfig.length) {
          throw new Error(`Strategy configuration is empty: ${selectedStrategy.code}`);
        }
        const configuredSignalGroups = buildSignalGroupsFromStrategyConfig(ruleConfig);
        const selectedFilters = buildBackendStrategySignalSelection(configuredSignalGroups, ruleConfig);
        const strategyLabel = String(strategyCode ?? selectedStrategy.label);

        setStrategyRuleConfig(ruleConfig);
        setActiveStrategyFilter(selectedStrategy.code);
        setSignalFilters(selectedFilters);
        setSelectedGuideStrategyCode(selectedStrategy.code);
        setSelectedStock(null);
        setActivePage("list");
        skipNextListFetch.current = true;
        await loadStrategyStocks({
          strategy: {
            ...advisorData?.strategyRecommendation,
            code: selectedStrategy.code,
            strategyCode: selectedStrategy.code,
            primaryStrategy: strategyLabel,
            label: strategyLabel,
            selectedStrategyLabel: strategyLabel,
            mode: selectedStrategy.code === "AVOID_WEAK" ? "Defensive" : advisorData?.strategyRecommendation?.mode,
          },
          signals: configuredSignalGroups,
          selectedFilters,
        });
      });
    } catch (err) {
      console.error("Unable to load strategy stock picks.", err);
      setError("Unable to load strategy configuration. Please try again later.");
    }
  };

  const refreshSelectedStockTradeability = async (stock) => {
    if (!stock || !signalGroupsForFilters.length) return;

    setStockRefreshLoading(true);
    setError("");
    try {
      const activeStrategy = activeStrategyFilter === "ALL"
        ? buildPrimaryStrategyContext().primaryStrategy
        : getStrategyByCode(activeStrategyFilter);
      const selectedFilters = buildBackendStrategySignalSelection(signalGroupsForFilters, strategyRuleConfig);
      const requestStrategy = {
        ...advisorData?.strategyRecommendation,
        code: activeStrategy.code,
        strategyCode: activeStrategy.code,
        primaryStrategy: activeStrategy.label,
        label: activeStrategy.label,
        selectedStrategyLabel: activeStrategy.label,
        mode: activeStrategy.code === "AVOID_WEAK" ? "Defensive" : advisorData?.strategyRecommendation?.mode,
      };
      const refreshed = await refreshOneTradeabilityCandidate({
        stock,
        strategy: requestStrategy,
        signals: signalGroupsForFilters,
        selectedFilters,
        pickSettings,
      });
      setMatchingStocks((current) => current.map((item) =>
        item.symbol === refreshed.symbol ? { ...item, ...refreshed } : item
      ));
      setSelectedStock((current) =>
        current?.symbol === refreshed.symbol ? { ...current, ...refreshed } : refreshed
      );
      setLastSelectedSymbol(refreshed.symbol);
    } catch (err) {
      console.error("Unable to refresh selected stock tradeability.", err);
      setError(err?.message || "Unable to refresh selected stock tradeability.");
    } finally {
      setStockRefreshLoading(false);
    }
  };

  const selectStock = (stock) => {
    setLastSelectedSymbol(stock?.symbol ?? null);
    setSelectedStock(stock);
  };

  const selectedStockIndex = selectedStock
    ? filteredStocks.findIndex((stock) => stock.symbol === selectedStock.symbol)
    : -1;

  const showAdjacentStock = (direction) => {
    if (selectedStockIndex < 0) return;
    const nextStock = filteredStocks[selectedStockIndex + direction];
    if (nextStock) selectStock(nextStock);
  };

  const hasActiveListFilters = Boolean(searchTerm.trim() || priceFilter.min !== "" || priceFilter.max !== "" || sortMode !== "CONFIDENCE");

  return (
    <div className="app-shell">
      {candidateMaskVisible && <TradeCandidatesLoadingMask />}
      <Header
        activePage={activePage}
        loading={loading || stocksLoading || candidateMaskVisible}
        marketBias={advisorData?.marketBias}
        strategy={advisorData?.strategyRecommendation}
        onNavigate={navigatePage}
      />

      <main className="desk-main">
        {error && <div className="desk-error">{error}</div>}

        {activePage === "guide" && (
          <>
            <SignalLogicGuide
              signals={signalGroupsForFilters}
              selected={signalFilters}
              scoringConfig={scoringConfig}
              minimumScore={minimumScore}
              strategyRuleConfig={strategyRuleConfig}
              signalLabData={signalLabData}
              strategy={advisorData?.strategyRecommendation}
              marketBias={advisorData?.marketBias}
              selectedStrategyCode={selectedGuideStrategyCode}
              onConfigChange={setScoringConfig}
              onMinimumScoreChange={setMinimumScore}
              onStrategyLogicChange={changeGuideStrategyLogic}
              onApplyStrategySignals={applyGuideStrategySignals}
              onRevalidate={previewSignalStrategy}
              revalidating={stocksLoading}
              preview={strategyPreview}
              drafts={strategyDrafts}
              editingDraft={editingStrategyDraft}
              onEditDraft={editSignalStrategyDraft}
              onSaveDraft={saveSignalStrategyDraft}
              onActivateDraft={activateSignalStrategyDraft}
              onSimulateDraft={simulateSignalStrategyDraft}
              simulatingDraftCode={simulatingStrategyDraft}
              draftSimulation={strategyDraftSimulation}
              savingDraft={savingStrategyDraft}
            >
              <SignalFilterPanel
                signals={signalGroupsForFilters}
                selected={signalFilters}
                onChange={setSignalFilters}
                source={advisorData?.signalSource}
              />
            </SignalLogicGuide>
          </>
        )}

        {activePage === "list" && (
          <>
            {selectedStock ? (
              <StockDetailPage
                stock={selectedStock}
                onBack={() => setSelectedStock(null)}
                onPrevious={() => showAdjacentStock(-1)}
                onNext={() => showAdjacentStock(1)}
                hasPrevious={selectedStockIndex > 0}
                hasNext={selectedStockIndex >= 0 && selectedStockIndex < filteredStocks.length - 1}
                onRefreshTradeability={refreshSelectedStockTradeability}
                refreshingTradeability={stockRefreshLoading}
              />
            ) : (
            <section className="stock-section" aria-busy={stocksLoading ? "true" : "false"}>
              <div className="list-context-bar">
                <div>
                  <span>Active Strategy</span>
                  <strong>
                    {activeStrategyFilter !== "ALL"
                      ? resolveActiveStrategy(activeStrategyFilter).label
                      : advisorData?.strategyRecommendation?.primaryStrategy ?? "Strategy pending"}
                  </strong>
                </div>
                <div>
                  <span>Shown</span>
                  <strong>{stocksLoading ? "..." : filteredStocks.length}</strong>
                </div>
                <div>
                  <span>Market</span>
                  <strong>{formatMarketBias(advisorData?.marketBias?.bias)}</strong>
                </div>
                <div>
                  <span>Risk</span>
                  <strong>{formatRiskLevel(advisorData?.marketBias?.riskLevel)}</strong>
                </div>
                <div>
                  <span>India</span>
                  <strong>{advisorData?.marketBias?.indiaBias ?? "-"}</strong>
                </div>
                <div>
                  <span>Mode</span>
                  <strong>{advisorData?.strategyRecommendation?.mode ?? "-"}</strong>
                </div>
                <div>
                  <span>Min Score</span>
                  <strong>{advisorData?.strategyRecommendation?.minScore ?? "-"}</strong>
                </div>
                <div>
                  <span>Position</span>
                  <strong>{advisorData?.strategyRecommendation?.positionSizing ?? "-"}</strong>
                </div>
              </div>

              <div className="opportunity-backend-filter">
                <label className="opportunity-strategy-override">
                  <span>Opportunity Strategy</span>
                  <select
                    value={opportunityStrategyCode}
                    onChange={(event) => overrideOpportunityStrategy(event.target.value)}
                    disabled={stocksLoading || candidateMaskVisible}
                  >
                    {activeStrategies.map((strategy) => (
                      <option key={strategy.strategyCode} value={strategy.strategyCode}>
                        {strategy.strategyName ?? strategy.strategyCode}
                      </option>
                    ))}
                  </select>
                  <small>Overrides the market-bias primary strategy for this Opportunities load.</small>
                </label>
                <PickSettingsPanel
                  pickSettings={pickSettings}
                  onChange={setPickSettings}
                  onReload={reloadOpportunityCandidates}
                  reloading={stocksLoading || candidateMaskVisible}
                />
              </div>

              <div className="price-filter-bar">
                <div className="price-filter-title">
                  <span>Price Range</span>
                  <strong className="price-range-ascii">Rs.{activePriceMin} - Rs.{activePriceMax}</strong>
                  <strong>{"\u20B9"}{activePriceMin} - {"\u20B9"}{activePriceMax}</strong>
                </div>
                <label>
                  <span>From</span>
                  <input
                    type="range"
                    min={priceBounds.min}
                    max={priceBounds.max}
                    value={activePriceMin}
                    onChange={(event) => changePriceFilter("min", event.target.value)}
                    disabled={!visibleStocks.length}
                  />
                </label>
                <label>
                  <span>To</span>
                  <input
                    type="range"
                    min={priceBounds.min}
                    max={priceBounds.max}
                    value={activePriceMax}
                    onChange={(event) => changePriceFilter("max", event.target.value)}
                    disabled={!visibleStocks.length}
                  />
                </label>
              </div>

              <div className="list-tools">
                <div className="search-sort-row">
                  <label className="symbol-search">
                    <span>Search Symbol</span>
                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Search symbol"
                    />
                  </label>
                  <div className="sort-control" aria-label="Sort stocks">
                    {[
                      ["CONFIDENCE", "Confidence"],
                      ["PRICE", "Price"],
                      ["SYMBOL", "Symbol"],
                    ].map(([value, label]) => (
                      <button
                        className={sortMode === value ? "active" : ""}
                        key={value}
                        type="button"
                        onClick={() => setSortMode(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button
                    className="reset-filter-btn"
                    type="button"
                    onClick={resetListFilters}
                    disabled={!hasActiveListFilters}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {tradeabilityDesk && (
                <div className="tradeability-summary-strip">
                  <div className="tradeability-summary-card ready">
                    <span>Entry Ready</span>
                    <strong>{tradeabilityDesk.summary?.ENTRY_READY ?? 0}</strong>
                  </div>
                  <div className="tradeability-summary-card watch">
                    <span>Watch</span>
                    <strong>{tradeabilityDesk.summary?.WATCH_ONLY ?? 0}</strong>
                  </div>
                  <div className="tradeability-summary-card avoid">
                    <span>Avoid Intraday</span>
                    <strong>{tradeabilityDesk.summary?.AVOID_INTRADAY ?? 0}</strong>
                  </div>
                  <div className="tradeability-summary-card stale">
                    <span>Stale</span>
                    <strong>{tradeabilityDesk.summary?.STALE ?? 0}</strong>
                  </div>
                  <div className="tradeability-summary-card">
                    <span>Monitored</span>
                    <strong>{tradeabilityDesk.monitoredCount ?? filteredStocks.length}/{tradeabilityDesk.totalCandidates ?? filteredStocks.length}</strong>
                  </div>
                  <div className="tradeability-summary-card">
                    <span>Last Checked</span>
                    <strong>{tradeabilityDesk.lastCheckedAt ? tradeabilityDesk.lastCheckedAt.slice(11, 16) : "-"}</strong>
                  </div>
                </div>
              )}

              <div className="section-heading">
                <div>
                  <h2>Tradeability Decision Desk</h2>
                  <p>{stocksLoading ? "Loading tradeability checks..." : `${filteredStocks.length} monitored stocks shown${activeStrategyFilter === "ALL" ? "" : ` for ${activeStrategyFilter}`}`}</p>
                </div>
                {strategyPreview && (
                  <button className="reset-filter-btn" type="button" onClick={() => navigatePage("guide")}>
                    Back to Signal Lab
                  </button>
                )}
              </div>

              {filteredStocks.length ? (
                <div className="sector-list">
                  {Object.values(groupedStocks).map((sector) => (
                    <section className="sector-group" key={sector.name}>
                      <button className="sector-header group-toggle" type="button" onClick={() => toggleSector(sector.name)}>
                        <span className="group-title"><strong>{sector.name}</strong><em>{sector.count} stocks</em></span>
                        <span className="group-icon">{collapsedSectors[sector.name] ? ">" : "v"}</span>
                      </button>
                      {!collapsedSectors[sector.name] && (
                        <div className="industry-list">
                          {Object.values(sector.industries).map((industry) => {
                            const industryKey = `${sector.name}-${industry.name}`;
                            return (
                              <section className="industry-group" key={industryKey}>
                                <button className="industry-header group-toggle" type="button" onClick={() => toggleIndustry(industryKey)}>
                                  <span className="group-title"><strong>{industry.name}</strong><em>{industry.count} stocks</em></span>
                                  <span className="group-icon">{collapsedIndustries[industryKey] ? ">" : "v"}</span>
                                </button>
                                {!collapsedIndustries[industryKey] && (
                                  <div className="stock-board">
                                    <div className="stock-board-head">
                                      <span>Symbol</span>
                                      <span>Strategy / Action</span>
                                      <span>Score / Signals</span>
                                      <span>Price</span>
                                      <span>Status</span>
                                      <span>Tradeability</span>
                                      <span>Volume</span>
                                      <span>Checked</span>
                                    </div>
                                    <div className="stock-list">
                                    {industry.stocks.map((stock) => (
                                      <StockCard
                                        stock={stock}
                                        key={stock.symbol}
                                        onSelect={selectStock}
                                        selected={lastSelectedSymbol === stock.symbol}
                                      />
                                    ))}
                                    </div>
                                  </div>
                                )}
                              </section>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  {stocksLoading ? (
                    "Loading..."
                  ) : strategyPreview ? (
                    <>
                      <strong>No stocks matched the selected Signal Lab configuration.</strong>
                      <p>Your Signal Lab selections are preserved. Return to adjust them and run the check again.</p>
                      <button className="reset-filter-btn" type="button" onClick={() => navigatePage("guide")}>
                        Back to Signal Lab
                      </button>
                    </>
                  ) : (
                    <>
                      <strong>No stocks match the active filters.</strong>
                      <p>Reset price, search, or sort filters to return to the strategy list.</p>
                      <button className="reset-filter-btn" type="button" onClick={resetListFilters}>
                        Reset filters
                      </button>
                    </>
                  )}
                </div>
              )}
            </section>
            )}
          </>
        )}

        {activePage === "desk" && (
          <>
            <TradingTableStrip
              marketBias={advisorData?.marketBias}
              strategy={advisorData?.strategyRecommendation}
            />
            <DecisionDetailsCard
              marketBias={advisorData?.marketBias}
              strategy={advisorData?.strategyRecommendation}
              loading={loading}
              onStrategySelect={openStrategyList}
            />
          </>
        )}

        {activePage === "config" && <EventConfigPage />}
        {activePage === "marketBiasCalculator" && (
          <MarketBiasCalculatorPage
            marketBias={advisorData?.marketBias}
            marketBiasInputs={marketBiasInputs}
            onRefreshMarketInputs={refreshMarketInputsForCalculator}
            onMarketBiasApplied={applyMarketBiasSummary}
            refreshing={refreshingMarketInputs}
          />
        )}
      </main>
    </div>
  );
}

export default App;
